// ============================================
// Quiz Mode Component - Mode Selector & Router
// ============================================

import { lazy, Suspense } from 'preact/compat';
import { useSignal } from '@preact/signals';
import { m, AnimatePresence } from 'framer-motion';
import { tabContent } from '@/animations';
import { ProgressBar } from './ui/ProgressBar';
import type { QuizMode as QuizModeType, Word, Particle, GrammarPattern } from '@/types';
import { isKanaLevel, VOCAB_QUIZ_MODES, availableQuizModesForLevel } from '@/types';
import {
  currentQuizMode,
  setQuizMode,
  progressPercent,
  cardIndex,
  deck,
  kanaMode,
  toggleKana,
  currentCard,
  currentLevel,
  quizLength,
  quizAnsweredCount,
  quizWrapLimit
} from '@/state/signals';

// Lazy load quiz components for code splitting
const MultipleChoice = lazy(() => import('./quiz/MultipleChoice').then(m => ({ default: m.MultipleChoice })));
const SpeedChallenge = lazy(() => import('./quiz/SpeedChallenge').then(m => ({ default: m.SpeedChallenge })));
const ListeningFirst = lazy(() => import('./quiz/ListeningFirst').then(m => ({ default: m.ListeningFirst })));
const MixedChallenge = lazy(() => import('./quiz/MixedChallenge').then(m => ({ default: m.MixedChallenge })));
const KanjiOnly = lazy(() => import('./quiz/KanjiOnly').then(m => ({ default: m.KanjiOnly })));
const ParticleQuiz = lazy(() => import('./quiz/ParticleQuiz').then(m => ({ default: m.ParticleQuiz })));
const GrammarQuiz = lazy(() => import('./quiz/GrammarQuiz').then(m => ({ default: m.GrammarQuiz })));

// ============================================
// Quiz Mode Selector
// ============================================

interface QuizModeSelectorProps {
  currentMode: QuizModeType;
  onSelectMode: (mode: QuizModeType) => void;
  availableModes: QuizModeType[];
}

const QUIZ_MODES: { mode: QuizModeType; label: string; emoji: string }[] = [
  { mode: 'multiple-choice', label: 'Multiple Choice', emoji: '📝' },
  { mode: 'listening-first', label: 'Listening First', emoji: '🔊' },
  { mode: 'mixed-challenge', label: 'Mixed Challenge', emoji: '🔄' },
  { mode: 'speed-challenge', label: 'Speed Challenge', emoji: '⚡' },
  { mode: 'kanji-only', label: 'Kanji Only', emoji: '漢' },
  { mode: 'particle-quiz', label: 'Particle Quiz', emoji: '🔗' },
  { mode: 'grammar-quiz', label: 'Grammar', emoji: '📖' }
];

function QuizModeSelector({ currentMode, onSelectMode, availableModes }: QuizModeSelectorProps) {
  const visibleModes = QUIZ_MODES.filter(m => availableModes.includes(m.mode));
  return (
    <div className="flex flex-wrap gap-2 justify-center mb-6" role="radiogroup" aria-label="Quiz mode">
      {visibleModes.map(({ mode, label, emoji }) => (
        <m.button
          key={mode}
          type="button"
          onClick={() => onSelectMode(mode)}
          className={`
            px-3 py-2 rounded-lg text-sm font-medium
            transition-colors
            ${currentMode === mode
              ? 'bg-primary text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          `}
          role="radio"
          aria-checked={currentMode === mode}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <span className="mr-1">{emoji}</span>
          <span className="hidden sm:inline">{label}</span>
        </m.button>
      ))}
    </div>
  );
}

// ============================================
// Kana Toggle
// ============================================

function KanaToggle() {
  const showKana = kanaMode.value;

  return (
    <div className="flex items-center justify-center gap-2 mb-4">
      <label className="flex items-center gap-2 cursor-pointer">
        <div className="relative">
          <input
            type="checkbox"
            checked={showKana}
            onChange={() => toggleKana()}
            className="sr-only"
          />
          <div
            className={`
              w-10 h-6 rounded-full transition-colors
              ${showKana ? 'bg-primary' : 'bg-gray-300'}
            `}
          />
          <div
            className={`
              absolute top-1 left-1 w-4 h-4 bg-white rounded-full
              transition-transform shadow
              ${showKana ? 'translate-x-4' : ''}
            `}
          />
        </div>
        <span className="text-sm text-gray-600">
          Show: {showKana ? 'Kana' : 'No hints'}
        </span>
      </label>
    </div>
  );
}

// ============================================
// Quiz Length Chooser (flag-gated)
// ============================================

const QUIZ_LENGTH_OPTIONS: Array<{ value: number | null; label: string }> = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: null, label: 'All' }
];

interface QuizLengthChooserProps {
  selected: number | null;
  onSelect: (length: number | null) => void;
  onStart: (length: number | null) => void;
}

function QuizLengthChooser({ selected, onSelect, onStart }: QuizLengthChooserProps) {
  return (
    <m.div
      variants={tabContent}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="flex flex-col items-center justify-center min-h-[300px] text-center px-4 gap-6"
    >
      <div>
        <h3 className="text-xl font-bold text-slate-700 mb-1">How many questions?</h3>
        <p className="text-sm text-slate-500">Choose the length of this quiz session.</p>
      </div>
      <div className="flex gap-3 flex-wrap justify-center" role="radiogroup" aria-label="Quiz length">
        {QUIZ_LENGTH_OPTIONS.map(({ value, label }) => (
          <m.button
            key={label}
            type="button"
            role="radio"
            aria-checked={selected === value}
            onClick={() => onSelect(value)}
            className={`
              px-5 py-3 rounded-lg text-base font-semibold
              transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary
              ${selected === value
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
          >
            {label}
          </m.button>
        ))}
      </div>
      <m.button
        type="button"
        onClick={() => onStart(selected)}
        className="btn-primary px-7 py-2.5 text-base font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        Start Quiz
      </m.button>
    </m.div>
  );
}

// ============================================
// Loading Fallback
// ============================================

function QuizLoading() {
  return (
    <div className="flex items-center justify-center min-h-[300px]">
      <div className="text-gray-500 animate-pulse">Loading quiz...</div>
    </div>
  );
}

// ============================================
// Main Quiz Mode Component
// ============================================

interface QuizModeProps {
  allWords: Word[];
  particles: Particle[];
  grammar?: GrammarPattern[];
}

export function QuizMode({ allWords, particles, grammar = [] }: QuizModeProps) {
  const mode = currentQuizMode.value;
  const progress = progressPercent.value;
  const idx = cardIndex.value;
  const fullDeck = deck.value;
  const card = currentCard.value;
  const level = currentLevel.value;
  const onKanaLevel = isKanaLevel(level);

  const availableModes: QuizModeType[] = availableQuizModesForLevel(level);

  // ── Feature-flagged session controls ──────────────────────────────────
  // `quizStarted` is local to this component instance. It resets to false
  // whenever the component unmounts (mode switch to study + back) or when
  // the user picks a different quiz mode from the selector.
  // `pendingLength` tracks the chooser selection before the user hits Start.
  const sessionControlsOn = import.meta.env.VITE_FEATURE_SESSION_CONTROLS === 'true';
  const quizStarted = useSignal(false);
  const pendingLength = useSignal<number | null>(20);

  // Stable slice: computed once when the quiz starts, stored in a signal so
  // it doesn't re-derive on every render cycle. The slice is derived from the
  // deck at the moment the user clicks "Start Quiz". Re-presented chooser
  // (mode switch) always recalculates with the current deck.
  const slicedDeck = useSignal<Word[]>(fullDeck);

  // Wrap-proof completion tracking. `nextCard({source:'quiz'})` advances
  // cardIndex with MODULO over the FULL deck, so when the chosen length ≥ deck
  // size (including "All"), cardIndex wraps to 0 instead of reaching
  // slicedDeck.length — a raw `idx >= slicedDeck.length` guard would never fire
  // and the quiz would loop forever. Instead completion is keyed off the number
  // of questions *answered*: `quizAnsweredCount` (bumped by useQuizAnswer as it
  // auto-advances) counts only accepted answers/timeouts, never plain arrow
  // navigation — so browsing the deck can't consume the session. Reset to 0 at
  // Start Quiz; completion is `quizAnsweredCount >= slicedDeck.length`.
  const answeredCount = quizAnsweredCount.value;

  const handleSelectMode = (newMode: QuizModeType) => {
    setQuizMode(newMode);
    if (sessionControlsOn) {
      // Re-present the chooser for the new mode; reset quiz length to default.
      quizStarted.value = false;
      pendingLength.value = 20;
      quizLength.value = 20;
      quizWrapLimit.value = null;
    }
  };

  const handleStartQuiz = (length: number | null) => {
    quizLength.value = length;
    // Compute the stable slice now, once, from the current deck snapshot.
    const cap = length === null ? fullDeck.length : Math.min(length, fullDeck.length);
    slicedDeck.value = fullDeck.slice(0, cap);
    // Reset cardIndex so the session starts on the first card and clear the
    // answered tally so completion counts from zero for this session. Bound
    // arrow navigation to the slice for the session's duration.
    cardIndex.value = 0;
    quizAnsweredCount.value = 0;
    quizWrapLimit.value = cap;
    quizStarted.value = true;
  };

  // When the flag is on and the sliced quiz has been exhausted (the user has
  // ANSWERED every card in the stable slice), reset back to the chooser so the
  // user can start a new session without switching quiz modes. Wrap-proof:
  // keyed off answeredCount (accepted answers only), not the wrapping idx, so
  // it fires exactly once the session's questions are all answered — and never
  // from arrow navigation, which doesn't bump the count. Pure derived-state.
  if (
    sessionControlsOn &&
    quizStarted.value &&
    slicedDeck.value.length > 0 &&
    answeredCount >= slicedDeck.value.length
  ) {
    quizStarted.value = false;
    pendingLength.value = 20;
    quizWrapLimit.value = null;
  }

  // When the flag is on and the quiz hasn't started yet, show the chooser.
  if (sessionControlsOn && !quizStarted.value) {
    return (
      <m.div
        variants={tabContent}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="w-full"
      >
        {/* Mode Selector — always visible */}
        <QuizModeSelector currentMode={mode} onSelectMode={handleSelectMode} availableModes={availableModes} />

        <AnimatePresence mode="wait">
          <QuizLengthChooser
            key={mode}
            selected={pendingLength.value}
            onSelect={(v) => { pendingLength.value = v; }}
            onStart={handleStartQuiz}
          />
        </AnimatePresence>
      </m.div>
    );
  }

  // Determine effective deck and count for the progress header.
  // Flag on: use the stable slicedDeck computed at start time.
  // Flag off: walk the full deck exactly as before.
  const activeDeckForQuiz = sessionControlsOn ? slicedDeck.value : fullDeck;
  const effectiveDeckSize = activeDeckForQuiz.length;

  // Clamp the progress percentage to the sliced deck size when flag on.
  const effectiveProgress = effectiveDeckSize > 0
    ? ((idx + 1) / effectiveDeckSize) * 100
    : 0;
  const displayProgress = sessionControlsOn ? effectiveProgress : progress;

  const renderQuiz = () => {
    const quizKey = `${mode}-${card?.japanese ?? 'empty'}`;
    switch (mode) {
      case 'multiple-choice':
        return <MultipleChoice key={quizKey} allWords={allWords} />;
      case 'speed-challenge':
        return <SpeedChallenge key={quizKey} allWords={allWords} />;
      case 'listening-first':
        return <ListeningFirst key={quizKey} allWords={allWords} />;
      case 'mixed-challenge':
        return <MixedChallenge key={quizKey} allWords={allWords} />;
      case 'kanji-only':
        return <KanjiOnly key={quizKey} allWords={allWords} />;
      case 'particle-quiz':
        return <ParticleQuiz key={quizKey} particles={particles} />;
      case 'grammar-quiz':
        return <GrammarQuiz key={quizKey} patterns={grammar} />;
      default:
        return <MultipleChoice key={quizKey} allWords={allWords} />;
    }
  };

  return (
    <m.div
      variants={tabContent}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="w-full"
    >
      {/* Mode Selector */}
      <QuizModeSelector currentMode={mode} onSelectMode={handleSelectMode} availableModes={availableModes} />

      {/* Kana Toggle (vocabulary quizzes only; not the fill-blank particle/grammar
          modes, and hidden on kana levels where reading == character) */}
      {VOCAB_QUIZ_MODES.includes(mode) && !onKanaLevel && <KanaToggle />}

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Question {idx + 1} of {effectiveDeckSize}</span>
          <span>{Math.round(displayProgress)}%</span>
        </div>
        <ProgressBar value={displayProgress} />
      </div>

      {/* Quiz Content */}
      <Suspense fallback={<QuizLoading />}>
        <AnimatePresence mode="wait">
          <m.div
            key={mode}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderQuiz()}
          </m.div>
        </AnimatePresence>
      </Suspense>

      {/* Keyboard shortcuts hint */}
      <div className="text-center text-xs text-gray-400 mt-6">
        <span className="hidden sm:inline">
          Keyboard: 1-4 (select answer) • K (toggle kana) • ←/→ (navigate)
        </span>
      </div>
    </m.div>
  );
}
